import {useTranslation} from "react-i18next";
import EntityCRUDTable from "./EntityCRUDTable";
import {questionnaireCRUD} from "../../../http";
import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {getFilials, getDepartments} from "../../../redux/actions";
import {Typography, Box, Accordion, AccordionSummary, AccordionDetails, TextField, Alert, Chip, Button, Paper} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import WarningIcon from "@mui/icons-material/Warning";
import CodeIcon from "@mui/icons-material/Code";

export default function QuestionnaireCRUDTable({...props}) {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const {filials = []} = useSelector((state) => state.filial || {});
    const {departments = []} = useSelector((state) => state.department || {});
    const [entity, setEntity] = useState({
        name: "",
        value: null,
        level: 0,
        formId: null,
        parentId: null,
        filialId: null,
        departmentId: null
    });
    useEffect(() => {
        if (!filials.length) dispatch(getFilials({paging: {pageSize: 1000, pageNumber: 1, returnCount: false}}));
        if (!departments.length) dispatch(getDepartments({paging: {pageSize: 1000, pageNumber: 1, returnCount: false}}));
    }, [dispatch, filials.length, departments.length]);
    const [showFullJson, setShowFullJson] = useState(false);
    const [showRawJson, setShowRawJson] = useState(false);

    const setValue = (key, value) => {
        setEntity({...entity, [key]: value});
    };

    const clearEntity = () => {
        setEntity({
            name: "",
            value: null,
            level: 0,
            formId: null,
            parentId: null,
            filialId: null,
            departmentId: null
        });
    };

    const columns = [
        {
            title: "Филиал",
            dataIndex: "filial",
            key: "filialName",
            render: (_, row) => {
                // Сначала проверяем, есть ли прямое поле filial
                if (row.filial?.name) return row.filial.name;
                
                // Пытаемся извлечь filialId из generalInfo в value
                let filialId = row.filialId;
                if (!filialId && row.value) {
                    try {
                        let valueData = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
                        if (valueData && valueData.generalInfo && valueData.generalInfo.filialId) {
                            filialId = valueData.generalInfo.filialId;
                        }
                    } catch (e) {
                        // Если не удалось распарсить, используем row.filialId
                    }
                }
                
                if (filialId) {
                    const found = filials.find((f) => Number(f.id) === Number(filialId));
                    return found?.name || `ID: ${filialId}`;
                }
                return "-";
            }
        },
        {
            title: "Лаборатория",
            dataIndex: "department",
            key: "departmentName",
            render: (_, row) => {
                // Сначала проверяем, есть ли прямое поле department
                if (row.department?.name) return row.department.name;
                
                // Пытаемся извлечь departmentId из generalInfo в value
                let departmentId = row.departmentId;
                if (!departmentId && row.value) {
                    try {
                        let valueData = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
                        if (valueData && valueData.generalInfo && valueData.generalInfo.departmentId) {
                            departmentId = valueData.generalInfo.departmentId;
                        }
                    } catch (e) {
                        // Если не удалось распарсить, используем row.departmentId
                    }
                }
                
                if (departmentId) {
                    const found = departments.find((d) => Number(d.id) === Number(departmentId));
                    return found?.name || `ID: ${departmentId}`;
                }
                return "-";
            }
        },
        {
            title: "Дата создания",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value, row) => {
                // Сначала проверяем дату из generalInfo в value
                let dateValue = value;
                if (!dateValue && row.value) {
                    try {
                        let valueData = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
                        if (valueData && valueData.generalInfo && valueData.generalInfo.date) {
                            dateValue = valueData.generalInfo.date;
                        }
                    } catch (e) {
                        // Если не удалось распарсить, используем value (createdAt)
                    }
                }
                
                if (dateValue) {
                    try {
                        return new Date(dateValue).toLocaleDateString('ru-RU');
                    } catch (e) {
                        return dateValue || "-";
                    }
                }
                return "-";
            }
        },
        {
            title: "Год",
            dataIndex: "createdAt",
            key: "year",
            render: (value, row) => {
                // Сначала проверяем дату из generalInfo в value
                let dateValue = value;
                if (!dateValue && row.value) {
                    try {
                        let valueData = typeof row.value === 'string' ? JSON.parse(row.value) : row.value;
                        if (valueData && valueData.generalInfo && valueData.generalInfo.date) {
                            dateValue = valueData.generalInfo.date;
                        }
                    } catch (e) {
                        // Если не удалось распарсить, используем value (createdAt)
                    }
                }
                
                if (dateValue) {
                    try {
                        return new Date(dateValue).getFullYear();
                    } catch (e) {
                        return "-";
                    }
                }
                return "-";
            }
        },
    ];

    const filterFields = [
        {
            title: "Год (от)",
            key: "createdAt",
            operandPosition: 1,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
        {
            title: "Год (до)",
            key: "createdAt",
            operandPosition: 2,
            compareType: "between",
            type: "date2",
            filterType: "normal",
        },
        {
            title: "Филиал",
            key: "filialId",
            compareType: "equals",
            reducerKey: "filial",
            reducerArrayKey: "filials",
            reducerAction: getFilials,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
        {
            title: "Лаборатория",
            key: "departmentId",
            compareType: "equals",
            reducerKey: "department",
            reducerArrayKey: "departments",
            reducerAction: getDepartments,
            operandPosition: 1,
            optionLabelKey: "name",
            type: "normal",
            filterType: "normal",
        },
    ];

    // Проверка на "мусорные" данные (React Native event объекты)
    const isInvalidData = (obj) => {
        if (!obj || typeof obj !== 'object') return false;
        const suspiciousKeys = ['__nativeTag', '_viewConfig', '__internalInstanceHandle', '_targetInst', 'nativeEvent', 'touchHistory', '_dispatchInstances'];
        return suspiciousKeys.some(key => key in obj);
    };

    // Подсчет размера объекта
    const getObjectSize = (obj) => {
        try {
            return JSON.stringify(obj).length;
        } catch {
            return 0;
        }
    };

    // Специальное отображение общей информации
    const renderGeneralInfo = (generalInfo) => {
        if (!generalInfo || typeof generalInfo !== 'object') {
            return <Typography variant="body2" color="text.secondary">Нет данных</Typography>;
        }

        // Маппинг полей для читаемого отображения
        const fieldLabels = {
            date: 'Дата',
            email: 'Email',
            country: 'Страна',
            filialId: 'Филиал',
            departmentId: 'Лаборатория',
            assessor1: 'Оценщик 1',
            assessor2: 'Оценщик 2',
            staffCount: 'Количество сотрудников',
            hasISO15189: 'Наличие ISO 15189',
            labHeadName: 'Руководитель лаборатории',
            phoneNumber: 'Номер телефона',
            respondent1: 'Респондент 1',
            respondent2: 'Респондент 2',
            respondent3: 'Респондент 3',
            respondent4: 'Респондент 4',
            respondent5: 'Респондент 5',
            avgSamplesPerDay: 'Среднее количество проб в день',
            qualityManagerTrained: 'Обучен менеджер по качеству',
            safetyEngineerTrained: 'Обучен инженер по безопасности',
            hasEmergencySituations: 'Наличие экстренных ситуаций'
        };

        // Функция для получения значения поля
        const getFieldValue = (key, value) => {
            if (value === null || value === undefined || value === '') {
                return '-';
            }

            // Специальная обработка для филиала
            if (key === 'filialId' && value) {
                const found = filials.find((f) => Number(f.id) === Number(value));
                return found?.name || `ID: ${value}`;
            }

            // Специальная обработка для лаборатории
            if (key === 'departmentId' && value) {
                const found = departments.find((d) => Number(d.id) === Number(value));
                return found?.name || `ID: ${value}`;
            }

            // Специальная обработка для даты
            if (key === 'date' && value) {
                try {
                    return new Date(value).toLocaleString('ru-RU');
                } catch (e) {
                    return String(value);
                }
            }

            // Специальная обработка для boolean значений
            if (typeof value === 'boolean') {
                return value ? 'Да' : 'Нет';
            }

            return String(value);
        };

        // Сортируем поля: сначала важные, потом остальные
        const importantFields = ['date', 'country', 'filialId', 'departmentId', 'labHeadName', 'hasISO15189'];
        const sortedEntries = Object.entries(generalInfo).sort(([keyA], [keyB]) => {
            const aImportant = importantFields.includes(keyA);
            const bImportant = importantFields.includes(keyB);
            if (aImportant && !bImportant) return -1;
            if (!aImportant && bImportant) return 1;
            return keyA.localeCompare(keyB);
        });

        return (
            <Box>
                {sortedEntries.map(([key, value]) => {
                    const label = fieldLabels[key] || key;
                    const displayValue = getFieldValue(key, value);
                    const isImportant = importantFields.includes(key);

                    return (
                        <TextField
                            key={key}
                            margin="dense"
                            value={displayValue}
                            fullWidth
                            label={label}
                            disabled
                            sx={{
                                mb: 1,
                                '& .MuiInputBase-input': {
                                    fontWeight: isImportant ? 'bold' : 'normal'
                                }
                            }}
                        />
                    );
                })}
            </Box>
        );
    };

    // Специальное отображение структуры опросника
    const renderQuestionnaireStructure = (data) => {
        // Если данные пришли как строка JSON, парсим их
        if (typeof data === 'string') {
            try {
                data = JSON.parse(data);
            } catch (e) {
                console.error('Ошибка парсинга JSON:', e);
                return (
                    <Alert severity="error">
                        <Typography variant="body2">
                            Ошибка парсинга JSON данных. Проверьте формат данных.
                        </Typography>
                    </Alert>
                );
            }
        }

        if (!data || typeof data !== 'object') {
            return renderObjectRecursive(data, 0, 2);
        }

        // Проверяем, есть ли структура категорий
        if (data.categories && typeof data.categories === 'object') {
            const categories = data.categories;
            const sections = data.sections || {};
            const generalInfo = data.generalInfo || {};
            const pathogens = data.pathogens || {};

            // Определяем порядок категорий
            const categoryOrder = [
                'Общая информация',  // 0 - Общая информация
                'Патогены',          // Патогены
                'Помещения',         // 1-Помещения
                'Персонал',          // 2-Персонал
                'Правила лаб практики', // 3- Правила лаб практики
                'Уборка',            // 4-Уборка
                'Экстренные ситуации', // 5-Экстренные ситуации
                'Управление рисками', // 6-Управление рисками
                'Документация',      // 7-Документация
                'Биозащита',         // 8-Биозащита
                'Другие риски',      // 9-Другие риски
                'УББ-3'              // 10-УББЗ
            ];

           /////hello
            const sortedCategoryEntries = Object.entries(categories).sort(([keyA, catA], [keyB, catB]) => {
                const nameA = catA.name || keyA;
                const nameB = catB.name || keyB;
                const indexA = categoryOrder.indexOf(nameA);
                const indexB = categoryOrder.indexOf(nameB);
                
                // Если категории нет в списке порядка, помещаем в конец
                if (indexA === -1 && indexB === -1) return 0;
                if (indexA === -1) return 1;
                if (indexB === -1) return -1;
                
                return indexA - indexB;
            });

            return (
                <Box>
                    {/* Отображение общей информации, если есть (всегда первой) */}
                    {Object.keys(generalInfo).length > 0 && (
                        <Accordion sx={{mb: 2}} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                    0 - Общая информация
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {renderGeneralInfo(generalInfo)}
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Отображение патогенов (если есть на верхнем уровне) - второй в списке */}
                    {data.pathogens && typeof data.pathogens === 'object' && Object.keys(data.pathogens).length > 0 && !data.categories['Патогены'] && (
                        <Accordion sx={{mb: 2}} defaultExpanded={false}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                    Патогены ({Object.keys(data.pathogens).length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                    {Object.entries(data.pathogens).map(([pathogenName, level]) => (
                                        <Box key={pathogenName} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: '1px solid #e0e0e0'}}>
                                            <Typography variant="body2">{pathogenName}</Typography>
                                            <Chip 
                                                label={`Уровень ${level}`} 
                                                color={level === 4 ? 'error' : level === 3 ? 'warning' : 'default'}
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Отображение категорий в заданном порядке */}
                    {sortedCategoryEntries.map(([categoryKey, category]) => {
                        const categoryName = category.name || categoryKey;
                        const categoryId = category.id || categoryKey;
                        
                        // Определяем номер категории для отображения
                        let displayName = categoryName;
                        const orderIndex = categoryOrder.indexOf(categoryName);
                        if (orderIndex > 0) {
                            displayName = `${orderIndex} - ${categoryName}`;
                        }
                        
                        // Специальная обработка категории "Патогены"
                        if (category.pathogens && typeof category.pathogens === 'object') {
                            return (
                                <Accordion key={categoryKey} sx={{mb: 2}} defaultExpanded={false}>
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                        <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                            {displayName} ({Object.keys(category.pathogens).length})
                                        </Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                            {Object.entries(category.pathogens).map(([pathogenName, level]) => (
                                                <Box key={pathogenName} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: '1px solid #e0e0e0'}}>
                                                    <Typography variant="body2">{pathogenName}</Typography>
                                                    <Chip 
                                                        label={`Уровень ${level}`} 
                                                        color={level === 4 ? 'error' : level === 3 ? 'warning' : 'default'}
                                                        size="small"
                                                    />
                                                </Box>
                                            ))}
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            );
                        }
                        
                        // Специальная обработка категории "Общая информация" (пропускаем, так как она уже отображена выше)
                        if (category.generalInfo && typeof category.generalInfo === 'object') {
                            return null; // Уже отображено выше
                        }
                        
                        // Обычная категория с разделами
                        const categorySections = category.sections || {};
                        const sectionKeys = Object.keys(categorySections);
                        
                        if (sectionKeys.length === 0) {
                            return null;
                        }
                        
                        return (
                            <Accordion key={categoryKey} sx={{mb: 2}} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                        {displayName} ({sectionKeys.length} разделов)
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box>
                                        {Object.entries(categorySections).map(([sectionKey, section]) => {
                        const questions = section.questions || [];
                        const percentage = section.percentage;
                        const title = section.title || sectionKey;
                        const id = section.id || sectionKey;

                        return (
                            <Accordion key={sectionKey} sx={{mb: 1}} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2}}>
                                        <Box>
                                            <Typography variant="body1" sx={{fontWeight: 'bold'}}>
                                                {title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {id} | Вопросов: {questions.length}
                                            </Typography>
                                        </Box>
                                        {percentage !== null && percentage !== undefined && (
                                            <Chip 
                                                label={`${percentage}%`} 
                                                color={percentage >= 80 ? 'success' : percentage >= 60 ? 'warning' : 'error'}
                                                size="small"
                                                sx={{fontWeight: 'bold'}}
                                            />
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box>
                                        {questions.length > 0 ? (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                    Вопросы ({questions.length}):
                                                </Typography>
                                                {questions.map((question, qIndex) => {
                                                    // Извлечение текста вопроса из разных возможных полей
                                                    let questionText = 'Вопрос';
                                                    if (typeof question === 'object') {
                                                        questionText = question.text || 
                                                                      question.title || 
                                                                      question.question || 
                                                                      question.label || 
                                                                      question.name ||
                                                                      question.questionText ||
                                                                      question.id || 
                                                                      'Вопрос';
                                                    } else {
                                                        questionText = String(question);
                                                    }
                                                    
                                                    // Извлечение ответа из разных возможных полей
                                                    let answer = null;
                                                    if (typeof question === 'object') {
                                                        // Проверяем различные поля, где может быть ответ
                                                        answer = question.answer !== undefined ? question.answer :
                                                                 question.value !== undefined ? question.value :
                                                                 question.selected !== undefined ? question.selected :
                                                                 question.answerValue !== undefined ? question.answerValue :
                                                                 question.response !== undefined ? question.response :
                                                                 question.result !== undefined ? question.result :
                                                                 null;
                                                        
                                                        // Если ответ - объект, пытаемся извлечь значение
                                                        if (answer && typeof answer === 'object' && !Array.isArray(answer)) {
                                                            answer = answer.value !== undefined ? answer.value :
                                                                    answer.text !== undefined ? answer.text :
                                                                    answer.label !== undefined ? answer.label :
                                                                    answer;
                                                        }
                                                    }
                                                    
                                                    // Извлечение комментария
                                                    const comment = typeof question === 'object' ? question.comment : null;
                                                    
                                                    // Форматирование ответа для отображения
                                                    const answerText = answer !== null && answer !== undefined 
                                                        ? (typeof answer === 'boolean' ? (answer ? 'Да' : 'Нет') : 
                                                           Array.isArray(answer) ? answer.join(', ') :
                                                           String(answer))
                                                        : null;
                                                    
                                                    return (
                                                        <Accordion key={qIndex} sx={{mb: 1}} defaultExpanded={questions.length <= 5}>
                                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2}}>
                                                                    <Typography variant="body2">
                                                                        <strong>Вопрос {qIndex + 1}:</strong> {questionText}
                                                                    </Typography>
                                                                    {answerText !== null && (
                                                                        <Chip 
                                                                            label={answerText} 
                                            color={typeof answer === 'boolean' ? (answer ? 'success' : 'default') : 'primary'}
                                                                            size="small"
                                                                            sx={{ml: 2}}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                        Вопрос:
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{mb: 2, pl: 2}}>
                                                                        {questionText}
                                                                    </Typography>
                                                                    
                                                                    {answerText !== null ? (
                                                                        <>
                                                                            <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                                Ответ:
                                                                            </Typography>
                                                                            <Box sx={{pl: 2, mb: 2}}>
                                                                                <Chip 
                                                                                    label={answerText} 
                                                                                    color={typeof answer === 'boolean' ? (answer ? 'success' : 'default') : 'primary'}
                                                                                    size="medium"
                                                                                />
                                                                            </Box>
                                                                        </>
                                                                    ) : (
                                                                        <Typography variant="body2" color="text.secondary" sx={{pl: 2, mb: 2}}>
                                                                            Ответ не предоставлен
                                                                        </Typography>
                                                                    )}
                                                                    
                                                                    {comment && (
                                                                        <>
                                                                            <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                                Комментарий:
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{pl: 2, mb: 2, fontStyle: 'italic', color: 'text.secondary'}}>
                                                                                {comment}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    
                                                                    {typeof question === 'object' && Object.keys(question).filter(k => !['text', 'title', 'question', 'label', 'id', 'answer', 'value', 'selected'].includes(k)).length > 0 && (
                                                                        <Box sx={{mt: 2}}>
                                                                            <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                                Дополнительные данные:
                                                                            </Typography>
                                                                            {renderObjectRecursive(
                                                                                Object.fromEntries(
                                                                                    Object.entries(question).filter(([k]) => !['text', 'title', 'question', 'label', 'id', 'answer', 'value', 'selected'].includes(k))
                                                                                ),
                                                                                0,
                                                                                2
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Вопросы отсутствуют
                                            </Typography>
                                        )}
                                        
                                        {Object.keys(section).filter(k => !['questions', 'percentage', 'title', 'id', 'category', 'categoryName'].includes(k)).length > 0 && (
                                            <Box sx={{mt: 2}}>
                                                <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                    Дополнительные данные:
                                                </Typography>
                                                {renderObjectRecursive(
                                                    Object.fromEntries(
                                                        Object.entries(section).filter(([k]) => !['questions', 'percentage', 'title', 'id', 'category', 'categoryName'].includes(k))
                                                    ),
                                                    0,
                                                    2
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                                        })}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            );
        }

        // Проверяем, есть ли структура опросника (sections) без категорий (обратная совместимость)
        if (data.sections && typeof data.sections === 'object' && (!data.categories || Object.keys(data.categories).length === 0)) {
            const sections = data.sections;
            const generalInfo = data.generalInfo || {};

            return (
                <Box>
                    {Object.keys(generalInfo).length > 0 && (
                        <Accordion sx={{mb: 2}} defaultExpanded>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                    0 - Общая информация
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {renderGeneralInfo(generalInfo)}
                            </AccordionDetails>
                        </Accordion>
                    )}

                    {/* Отображение патогенов, если есть */}
                    {data.pathogens && typeof data.pathogens === 'object' && Object.keys(data.pathogens).length > 0 && (
                        <Accordion sx={{mb: 2}} defaultExpanded={false}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h6" sx={{fontWeight: 'bold'}}>
                                    Патогены ({Object.keys(data.pathogens).length})
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1}}>
                                    {Object.entries(data.pathogens).map(([pathogenName, level]) => (
                                        <Box key={pathogenName} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderBottom: '1px solid #e0e0e0'}}>
                                            <Typography variant="body2">{pathogenName}</Typography>
                                            <Chip 
                                                label={`Уровень ${level}`} 
                                                color={level === 4 ? 'error' : level === 3 ? 'warning' : 'default'}
                                                size="small"
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    )}

                    <Typography variant="h6" sx={{mb: 2, fontWeight: 'bold'}}>
                        Разделы опросника ({Object.keys(sections).length})
                    </Typography>

                    {Object.entries(sections).map(([sectionKey, section]) => {
                        const questions = section.questions || [];
                        const percentage = section.percentage;
                        const title = section.title || sectionKey;
                        const id = section.id || sectionKey;

                        return (
                            <Accordion key={sectionKey} sx={{mb: 1}} defaultExpanded={false}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2}}>
                                        <Box>
                                            <Typography variant="body1" sx={{fontWeight: 'bold'}}>
                                                {title}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                ID: {id} | Вопросов: {questions.length}
                                            </Typography>
                                        </Box>
                                        {percentage !== null && percentage !== undefined && (
                                            <Chip 
                                                label={`${percentage}%`} 
                                                color={percentage >= 80 ? 'success' : percentage >= 60 ? 'warning' : 'error'}
                                                size="small"
                                                sx={{fontWeight: 'bold'}}
                                            />
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Box>
                                        {questions.length > 0 ? (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                    Вопросы ({questions.length}):
                                                </Typography>
                                                {questions.map((question, qIndex) => {
                                                    // Извлечение текста вопроса из разных возможных полей
                                                    let questionText = 'Вопрос';
                                                    if (typeof question === 'object') {
                                                        questionText = question.text || 
                                                                      question.title || 
                                                                      question.question || 
                                                                      question.label || 
                                                                      question.name ||
                                                                      question.questionText ||
                                                                      question.id || 
                                                                      'Вопрос';
                                                    } else {
                                                        questionText = String(question);
                                                    }
                                                    
                                                    // Извлечение ответа из разных возможных полей
                                                    let answer = null;
                                                    if (typeof question === 'object') {
                                                        answer = question.answer !== undefined ? question.answer :
                                                                 question.value !== undefined ? question.value :
                                                                 question.selected !== undefined ? question.selected :
                                                                 question.answerValue !== undefined ? question.answerValue :
                                                                 question.response !== undefined ? question.response :
                                                                 question.result !== undefined ? question.result :
                                                                 null;
                                                        
                                                        if (answer && typeof answer === 'object' && !Array.isArray(answer)) {
                                                            answer = answer.value !== undefined ? answer.value :
                                                                    answer.text !== undefined ? answer.text :
                                                                    answer.label !== undefined ? answer.label :
                                                                    answer;
                                                        }
                                                    }
                                                    
                                                    const comment = typeof question === 'object' ? question.comment : null;
                                                    
                                                    const answerText = answer !== null && answer !== undefined 
                                                        ? (typeof answer === 'boolean' ? (answer ? 'Да' : 'Нет') : 
                                                           Array.isArray(answer) ? answer.join(', ') :
                                                           String(answer))
                                                        : null;
                                                    
                                                    return (
                                                        <Accordion key={qIndex} sx={{mb: 1}} defaultExpanded={questions.length <= 5}>
                                                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                                                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', pr: 2}}>
                                                                    <Typography variant="body2">
                                                                        <strong>Вопрос {qIndex + 1}:</strong> {questionText}
                                                                    </Typography>
                                                                    {answerText !== null && (
                                                                        <Chip 
                                                                            label={answerText} 
                                                                            color={typeof answer === 'boolean' ? (answer ? 'success' : 'default') : 'primary'}
                                                                            size="small"
                                                                            sx={{ml: 2}}
                                                                        />
                                                                    )}
                                                                </Box>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Box>
                                                                    <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                        Вопрос:
                                                                    </Typography>
                                                                    <Typography variant="body2" sx={{mb: 2, pl: 2}}>
                                                                        {questionText}
                                                                    </Typography>
                                                                    
                                                                    {answerText !== null ? (
                                                                        <>
                                                                            <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                                Ответ:
                                                                            </Typography>
                                                                            <Box sx={{pl: 2, mb: 2}}>
                                                                                <Chip 
                                                                                    label={answerText} 
                                                                                    color={typeof answer === 'boolean' ? (answer ? 'success' : 'default') : 'primary'}
                                                                                    size="medium"
                                                                                />
                                                                            </Box>
                                                                        </>
                                                                    ) : (
                                                                        <Typography variant="body2" color="text.secondary" sx={{pl: 2, mb: 2}}>
                                                                            Ответ не предоставлен
                                                                        </Typography>
                                                                    )}
                                                                    
                                                                    {comment && (
                                                                        <>
                                                                            <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                                Комментарий:
                                                                            </Typography>
                                                                            <Typography variant="body2" sx={{pl: 2, mb: 2, fontStyle: 'italic', color: 'text.secondary'}}>
                                                                                {comment}
                                                                            </Typography>
                                                                        </>
                                                                    )}
                                                                    
                                                                    {typeof question === 'object' && Object.keys(question).filter(k => !['text', 'title', 'question', 'label', 'id', 'answer', 'value', 'selected', 'comment'].includes(k)).length > 0 && (
                                                                        <Box sx={{mt: 2}}>
                                                                            <Typography variant="body2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                                                Дополнительные данные:
                                                                            </Typography>
                                                                            {renderObjectRecursive(
                                                                                Object.fromEntries(
                                                                                    Object.entries(question).filter(([k]) => !['text', 'title', 'question', 'label', 'id', 'answer', 'value', 'selected', 'comment'].includes(k))
                                                                                ),
                                                                                0,
                                                                                2
                                                                            )}
                                                                        </Box>
                                                                    )}
                                                                </Box>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    );
                                                })}
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="text.secondary">
                                                Вопросы отсутствуют
                                            </Typography>
                                        )}
                                        
                                        {Object.keys(section).filter(k => !['questions', 'percentage', 'title', 'id', 'category', 'categoryName'].includes(k)).length > 0 && (
                                            <Box sx={{mt: 2}}>
                                                <Typography variant="subtitle2" sx={{mb: 1, fontWeight: 'bold'}}>
                                                    Дополнительные данные:
                                                </Typography>
                                                {renderObjectRecursive(
                                                    Object.fromEntries(
                                                        Object.entries(section).filter(([k]) => !['questions', 'percentage', 'title', 'id', 'category', 'categoryName'].includes(k))
                                                    ),
                                                    0,
                                                    2
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                </Box>
            );
        }

        // Если нет структуры sections или categories, используем обычный рекурсивный рендеринг
        return renderObjectRecursive(data, 0, 2);
    };

    // Рекурсивное отображение объекта с ограничением глубины
    const renderObjectRecursive = (obj, depth = 0, maxDepth = 3, parentKey = '') => {
        if (depth > maxDepth) {
            return (
                <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                    ... (глубина превышена, используйте "Показать полный JSON")
                </Typography>
            );
        }

        if (obj === null || obj === undefined) {
            return <Typography variant="body2" color="text.secondary">null</Typography>;
        }

        if (Array.isArray(obj)) {
            if (obj.length === 0) {
                return <Typography variant="body2" color="text.secondary">[] (пустой массив)</Typography>;
            }
            return (
                <Box>
                    {obj.slice(0, 10).map((item, index) => (
                        <Accordion key={index} sx={{mb: 1}}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="body2">
                                    <strong>[{index}]:</strong> {typeof item === 'object' 
                                        ? (Array.isArray(item) ? `Массив (${item.length} элементов)` : 'Объект')
                                        : String(item).substring(0, 50)}
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                {typeof item === 'object' ? (
                                    renderObjectRecursive(item, depth + 1, maxDepth, `${parentKey}[${index}]`)
                                ) : (
                                    <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                        {String(item)}
                                    </Typography>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                    {obj.length > 10 && (
                        <Typography variant="caption" color="text.secondary" sx={{ml: 2}}>
                            ... и еще {obj.length - 10} элементов
                        </Typography>
                    )}
                </Box>
            );
        }

        if (typeof obj === 'object') {
            const entries = Object.entries(obj);
            if (entries.length === 0) {
                return <Typography variant="body2" color="text.secondary">{} (пустой объект)</Typography>;
            }

            // Сортируем ключи: сначала важные, потом остальные
            const importantKeys = ['sections', 'questions', 'answers', 'name', 'value', 'score', 'percentage', 'total'];
            const sortedEntries = entries.sort(([a], [b]) => {
                const aImportant = importantKeys.some(k => a.toLowerCase().includes(k));
                const bImportant = importantKeys.some(k => b.toLowerCase().includes(k));
                if (aImportant && !bImportant) return -1;
                if (!aImportant && bImportant) return 1;
                return a.localeCompare(b);
            });

            return (
                <Box>
                    {sortedEntries.slice(0, 20).map(([key, val]) => {
                        const isImportant = importantKeys.some(k => key.toLowerCase().includes(k));
                        const valType = typeof val === 'object' 
                            ? (Array.isArray(val) ? `Массив (${val.length})` : 'Объект')
                            : typeof val;
                        const valPreview = typeof val !== 'object' 
                            ? String(val).substring(0, 100)
                            : '';

                        return (
                            <Accordion key={key} sx={{mb: 0.5}} defaultExpanded={isImportant && depth === 0}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                                        <Typography variant="body2" sx={{fontWeight: isImportant ? 'bold' : 'normal'}}>
                                            <strong>{key}:</strong> {valType} {valPreview ? ` - ${valPreview}` : ''}
                                        </Typography>
                                        {isInvalidData(val) && (
                                            <Chip 
                                                icon={<WarningIcon />} 
                                                label="Подозрительные данные" 
                                                color="warning" 
                                                size="small"
                                            />
                                        )}
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    {typeof val === 'object' ? (
                                        renderObjectRecursive(val, depth + 1, maxDepth, `${parentKey}.${key}`)
                                    ) : (
                                        <Typography variant="body2" sx={{fontFamily: 'monospace', wordBreak: 'break-word'}}>
                                            {String(val)}
                                        </Typography>
                                    )}
                                </AccordionDetails>
                            </Accordion>
                        );
                    })}
                    {entries.length > 20 && (
                        <Typography variant="caption" color="text.secondary" sx={{ml: 2}}>
                            ... и еще {entries.length - 20} полей
                        </Typography>
                    )}
                </Box>
            );
        }

        return <Typography variant="body2" sx={{fontFamily: 'monospace'}}>{String(obj)}</Typography>;
    };


    // Извлекаем ID из value только один раз, чтобы избежать лишних перерендеров
    const extractedFilialId = useMemo(() => {
        if (entity.filialId) return entity.filialId;
        if (entity.filial?.id) return entity.filial.id;
        if (entity.value) {
            try {
                let valueData = typeof entity.value === 'string' ? JSON.parse(entity.value) : entity.value;
                if (valueData?.generalInfo?.filialId) {
                    return valueData.generalInfo.filialId;
                }
            } catch (e) {
                // Игнорируем ошибки парсинга
            }
        }
        return null;
    }, [entity.filialId, entity.filial?.id, entity.value]);

    const extractedDepartmentId = useMemo(() => {
        if (entity.departmentId) return entity.departmentId;
        if (entity.department?.id) return entity.department.id;
        if (entity.value) {
            try {
                let valueData = typeof entity.value === 'string' ? JSON.parse(entity.value) : entity.value;
                if (valueData?.generalInfo?.departmentId) {
                    return valueData.generalInfo.departmentId;
                }
            } catch (e) {
                // Игнорируем ошибки парсинга
            }
        }
        return null;
    }, [entity.departmentId, entity.department?.id, entity.value]);

    const filialDisplayName = useMemo(() => {
        if (entity.filial?.name) return entity.filial.name;
        if (extractedFilialId) {
            const found = filials.find((f) => Number(f.id) === Number(extractedFilialId));
            return found?.name || `ID: ${extractedFilialId}`;
        }
        return "-";
    }, [entity.filial?.name, extractedFilialId, filials]);

    const departmentDisplayName = useMemo(() => {
        if (entity.department?.name) return entity.department.name;
        if (extractedDepartmentId) {
            const found = departments.find((d) => Number(d.id) === Number(extractedDepartmentId));
            return found?.name || `ID: ${extractedDepartmentId}`;
        }
        return "-";
    }, [entity.department?.name, extractedDepartmentId, departments]);

    const detailsFields = useMemo(() => [
        <TextField
            key="name"
            margin="normal"
            value={entity.name || ""}
            fullWidth
            id="name"
            name="name"
            label="Название"
            disabled
        />,
        <TextField
            key="formName"
            margin="normal"
            value={entity.form?.name || "-"}
            fullWidth
            id="formName"
            name="formName"
            label="Форма анкеты"
            disabled
        />,
        <TextField
            key="filialName"
            margin="normal"
            value={filialDisplayName}
            fullWidth
            id="filialName"
            name="filialName"
            label="Филиал"
            disabled
        />,
        <TextField
            key="departmentName"
            margin="normal"
            value={departmentDisplayName}
            fullWidth
            id="departmentName"
            name="departmentName"
            label="Лаборатория"
            disabled
        />,
        <TextField
            key="createdAt"
            margin="normal"
            value={(() => {
                // Сначала проверяем дату из generalInfo в value
                let dateValue = entity.createdAt;
                if (!dateValue && entity.value) {
                    try {
                        let valueData = typeof entity.value === 'string' ? JSON.parse(entity.value) : entity.value;
                        if (valueData && valueData.generalInfo && valueData.generalInfo.date) {
                            dateValue = valueData.generalInfo.date;
                        }
                    } catch (e) {
                        // Если не удалось распарсить, используем entity.createdAt
                    }
                }
                
                if (dateValue) {
                    try {
                        return new Date(dateValue).toLocaleString('ru-RU');
                    } catch (e) {
                        return String(dateValue);
                    }
                }
                return "-";
            })()}
            fullWidth
            id="createdAt"
            name="createdAt"
            label="Дата создания"
            disabled
        />,
        <Box sx={{mt: 2}}>
            <Typography variant="h6" gutterBottom>
                Данные опросника (JSON)
            </Typography>
            {(() => {
                let value = entity.value;
                
                // Если value - строка JSON, парсим её
                if (typeof value === 'string') {
                    try {
                        value = JSON.parse(value);
                    } catch (e) {
                        console.error('Ошибка парсинга JSON в value:', e);
                        // Если не удалось распарсить, оставляем как строку
                    }
                }
                
                if (value === null || value === undefined) {
                    return <Typography variant="body2" color="text.secondary">Нет данных</Typography>;
                }

                const isInvalid = typeof value === 'object' && isInvalidData(value);
                const objectSize = typeof value === 'string' ? value.length : (typeof value === 'object' ? getObjectSize(value) : 0);
                const sizeKB = (objectSize / 1024).toFixed(2);

                return (
                    <Box>
                        {isInvalid && (
                            <Alert severity="warning" icon={<WarningIcon />} sx={{mb: 2}}>
                                <Typography variant="body2" gutterBottom>
                                    <strong>Внимание!</strong> Данные содержат подозрительные поля, похожие на внутренние объекты React Native.
                                </Typography>
                                <Typography variant="caption">
                                    Возможно, вместо данных опросника был сохранен объект события. Проверьте мобильное приложение.
                                </Typography>
                            </Alert>
                        )}

                        <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center'}}>
                            <Chip 
                                icon={<CodeIcon />} 
                                label={`Размер: ${sizeKB} KB`} 
                                size="small" 
                                variant="outlined"
                            />
                            <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => setShowRawJson(!showRawJson)}
                            >
                                {showRawJson ? 'Скрыть' : 'Показать'} полный JSON
                            </Button>
                            {!showRawJson && (
                                <Button 
                                    size="small" 
                                    variant="outlined"
                                    onClick={() => setShowFullJson(!showFullJson)}
                                >
                                    {showFullJson ? 'Скрыть' : 'Показать'} структурированный вид
                                </Button>
                            )}
                        </Box>

                        {showRawJson ? (
                            <Paper sx={{p: 2, maxHeight: '600px', overflow: 'auto', bgcolor: '#f5f5f5'}}>
                                <pre style={{
                                    whiteSpace: 'pre-wrap', 
                                    fontSize: '11px', 
                                    fontFamily: 'monospace',
                                    margin: 0,
                                    wordBreak: 'break-word'
                                }}>
                                    {JSON.stringify(value, null, 2)}
                                </pre>
                            </Paper>
                        ) : (
                            <Box sx={{maxHeight: '600px', overflow: 'auto'}}>
                                {typeof value === 'object' ? (
                                    renderQuestionnaireStructure(value)
                                ) : (
                                    <Typography variant="body2" sx={{fontFamily: 'monospace'}}>
                                        {String(value)}
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </Box>
                );
            })()}
        </Box>
    ], [entity.name, entity.form?.name, entity.createdAt, entity.value, showFullJson, showRawJson, filialDisplayName, departmentDisplayName]);

    const removeFields = useMemo(() => [
        <TextField
            key="name"
            margin="normal"
            value={entity.name || ""}
            fullWidth
            id="name"
            name="name"
            label="Название"
            disabled
        />,
        <TextField
            key="formName"
            margin="normal"
            value={entity.form?.name || "-"}
            fullWidth
            id="formName"
            name="formName"
            label="Форма анкеты"
            disabled
        />,
        <TextField
            key="filialName"
            margin="normal"
            value={filialDisplayName}
            fullWidth
            id="filialName"
            name="filialName"
            label="Филиал"
            disabled
        />,
        <TextField
            key="departmentName"
            margin="normal"
            value={departmentDisplayName}
            fullWidth
            id="departmentName"
            name="departmentName"
            label="Лаборатория"
            disabled
        />,
    ], [entity.name, entity.form?.name, filialDisplayName, departmentDisplayName]);

    return (
        <EntityCRUDTable
            entityType="questionnaire"
            columns={columns}
            filterFields={filterFields}
            entity={entity}
            setEntity={setEntity}
            setValue={setValue}
            clearEntity={clearEntity}
            detailsFields={detailsFields}
            removeFields={removeFields}
            canCreate={false}
            canEdit={false}
            canRemove={true}
            detailsEntityTitle="Просмотр вопросника"
            removeEntityTitle="Удаление вопросника"
            {...props}
        />
    );
}
